import {Component, computed, input} from '@angular/core';
import {LangNamePipe} from '../submit-po/tracks-selection/language-select/lang-name.pipe';

/** True for the ISO 639 "undetermined" code, with or without a private-use suffix (und-x-…). VTK-3123. */
export function isUndetermined(code: string | null | undefined): boolean {
  return !!code && code.split('-')[0].toLowerCase() === 'und';
}

/**
 * Track types whose und gets the badge (user decision 2026-10-13): audio and subtitles, burnt-in or not.
 * Closed captions are subtitle text in the same PO columns, so they count too. Video, data and image
 * tracks commonly carry no language and show "Undetermined" as plain text.
 */
const BADGED_TRACK_TYPES = new Set(['audio', 'subtitle', 'burntin_subtitle', 'closedcaption', 'burntin_closedcaption']);

/**
 * A track language as text. An undetermined language (und) is a warning badge with a tooltip, so a
 * audio or subtitle track nobody has set a language for stands out before it is published (VTK-3123).
 */
@Component({
  selector: 'app-language',
  standalone: true,
  template: `@if (undetermined()) {<span class="cl-badge cl-badge-sm cl-color-honey" [title]="hint">Undetermined ({{ code() }})</span>} @else {{{ label() }}}`,
})
export class LanguageComponent {
  readonly code = input<string | null | undefined>();
  /** The track's type (repository track_type or workflow codec_type); only audio and subtitle und get the badge. */
  readonly trackType = input<string | null | undefined>();
  /** Show the language name (from the language list) instead of the code. */
  readonly named = input(false);

  readonly hint = 'Language not set: set the track language before publishing';
  protected readonly undetermined = computed(() =>
    isUndetermined(this.code()) && BADGED_TRACK_TYPES.has(this.trackType() ?? ''));
  protected readonly label = computed(() => {
    const code = this.code() ?? '';
    // The language list only knows "und"; und-x-… and UND would come back raw from the exact lookup.
    if (isUndetermined(code)) return 'Undetermined';
    return this.named() ? new LangNamePipe().transform(code) : code;
  });
}
